import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { desktopRibbonInputClass } from '../components/ribbon/DesktopRibbonControls';

import { EnterprisePage } from '../components/layout/EnterprisePage';
import { EnterpriseBadge } from '../components/layout/EnterpriseTable';
import { DataGridEnterprise } from '../components/layout/DataGridEnterprise';
import type { DataGridColumn } from '../components/layout/DataGridEnterprise';
import { EnterpriseModal, ModalField, modalInputClass, modalSelectClass, ModalPrimaryButton, ModalSecondaryButton } from '../components/layout/EnterpriseModal';
import { KanbanBoard } from '../components/kanban/KanbanBoard';
import type { KanbanColumnConfig, KanbanCardData } from '../components/kanban/KanbanBoard';
import { ProblemDetailPanel } from '../components/problemas/ProblemDetailPanel';
import { ProblemAlertsBar } from '../components/problemas/ProblemAlertsBar';
import { useProblemasRibbon } from '../hooks/useProblemasRibbon';
import { desktopRibbonSelectClass } from '../components/ribbon/DesktopRibbonControls';
import { useAppToast } from '../components/AppToast';
import { useOrganizationId } from '../contexts/WorkspaceContext';
import { useExecutionSelection } from '../contexts/ExecutionSelectionContext';
import { useProjectContext } from '../context/ProjectContext';
import { IssueService } from '../services/issueService';
import type { IssueFilters } from '../services/issueService';
import type { StatusCapsuleItem } from '../components/layout/StatusCapsule';
import {
  ISSUE_STATUS_LABELS, ISSUE_TYPE_LABELS, ISSUE_SEVERITY_LABELS, ISSUE_MODE_LABELS,
  getStatusTransitions, getStageOrder,
} from '../types/issue';
import type { Issue, IssueStatus, IssueType, IssueSeverity, IssueMode, ProblemAlert, RecurrenceMatch } from '../types/issue';
import { exportToCSV, exportToPrintPDF } from '../utils/exportTableData';
import {
  BarChart3, Filter, X, ChevronDown, ChevronUp, Check,
  AlertCircle, Search, Crosshair, Clock, CheckCircle2, Shield, Eye, PlayCircle, ClipboardCheck,
  User, Calendar, LinkIcon, Building2,
} from 'lucide-react';

const SEVERITY_COLOR: Record<string, string> = {
  critical: 'bg-red-500/10 text-red-600 border border-red-500/20 dark:text-red-400',
  high: 'bg-orange-500/10 text-orange-600 border border-orange-500/20 dark:text-orange-400',
  medium: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
  low: 'bg-slate-500/10 text-slate-600 border border-slate-500/20 dark:text-slate-400',
};

const STATUS_COLOR: Record<string, string> = {
  open: 'bg-red-500/10 text-red-600 border border-red-500/20 dark:text-red-400',
  investigation: 'bg-amber-500/10 text-amber-600 border border-amber-500/20 dark:text-amber-400',
  root_cause: 'bg-orange-500/10 text-orange-600 border border-orange-500/20 dark:text-orange-400',
  action_plan: 'bg-blue-500/10 text-blue-600 border border-blue-500/20 dark:text-blue-400',
  execution: 'bg-indigo-500/10 text-indigo-600 border border-indigo-500/20 dark:text-indigo-400',
  validation: 'bg-purple-500/10 text-purple-600 border border-purple-500/20 dark:text-purple-400',
  monitoring: 'bg-cyan-500/10 text-cyan-600 border border-cyan-500/20 dark:text-cyan-400',
  closed: 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/20 dark:text-emerald-400',
  cancelled: 'bg-slate-500/10 text-slate-500 border border-slate-500/20 dark:text-slate-400',
};

const KANBAN_STATUSES: IssueStatus[] = ['open', 'investigation', 'root_cause', 'action_plan', 'execution', 'validation', 'monitoring', 'closed'];

const KANBAN_COLUMNS: KanbanColumnConfig[] = [
  { id: 'open', title: 'Aberto', icon: AlertCircle, headerBorder: 'border-t-red-500', badgeColor: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  { id: 'investigation', title: 'Investigação', icon: Search, headerBorder: 'border-t-amber-500', badgeColor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  { id: 'root_cause', title: 'Causa Raiz', icon: Crosshair, headerBorder: 'border-t-orange-500', badgeColor: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  { id: 'action_plan', title: 'Plano de Ação', icon: ClipboardCheck, headerBorder: 'border-t-blue-500', badgeColor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  { id: 'execution', title: 'Execução', icon: PlayCircle, headerBorder: 'border-t-indigo-500', badgeColor: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400' },
  { id: 'validation', title: 'Validação', icon: Shield, headerBorder: 'border-t-purple-500', badgeColor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' },
  { id: 'monitoring', title: 'Monitoramento', icon: Eye, headerBorder: 'border-t-cyan-500', badgeColor: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400' },
  { id: 'closed', title: 'Encerrado', icon: CheckCircle2, headerBorder: 'border-t-emerald-500', badgeColor: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' },
];

const EXPORT_COLUMNS = [
  { key: 'title', label: 'Título' },
  { key: 'type', label: 'Tipo' },
  { key: 'severity', label: 'Severidade' },
  { key: 'status', label: 'Status' },
  { key: 'assigned_to', label: 'Responsável' },
  { key: 'created_at', label: 'Criado em' },
];

const COLUMNS: DataGridColumn<Issue>[] = [
  { key: 'title', label: 'Título', sortable: true, render: (r) => r.title },
  { key: 'type', label: 'Tipo', sortable: true, render: (r) => ISSUE_TYPE_LABELS[r.type] },
  { key: 'severity', label: 'Severidade', sortable: true, render: (r) => <EnterpriseBadge label={ISSUE_SEVERITY_LABELS[r.severity]} color={SEVERITY_COLOR[r.severity]} /> },
  { key: 'status', label: 'Status', sortable: true, render: (r) => <EnterpriseBadge label={ISSUE_STATUS_LABELS[r.status]} color={STATUS_COLOR[r.status]} /> },
  { key: 'assigned_to', label: 'Responsável', render: (r) => r.assigned_to ?? '—' },
  { key: 'created_at', label: 'Criado em', mono: true, sortable: true, render: (r) => new Date(r.created_at).toLocaleDateString('pt-BR') },
];

/**
 * 🚨 ProblemasPage — Gestão de Problemas e Não-Conformidades
 */
export const ProblemasPage: React.FC = () => {
  const organizationId = useOrganizationId();
  const { selectedProject, setSelectedProject } = useProjectContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlPlanId = searchParams.get('planId');
  const { selectedProblemId, selectProblem } = useExecutionSelection();
  const { showToast, ToastContainer } = useAppToast();

  // ── UI State ───────────────────────────────────────
  const [viewMode, setViewMode] = useState<'list' | 'kanban'>('list');
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showDetailPanel, setShowDetailPanel] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // ── Create form state ──────────────────────────────
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState<IssueType>('incident');
  const [newSeverity, setNewSeverity] = useState<IssueSeverity>('medium');
  const [newDescription, setNewDescription] = useState('');
  const [newMode, setNewMode] = useState<IssueMode>('quick_correction');
  const [newDepartment, setNewDepartment] = useState('');

  // ── Filter state ───────────────────────────────────
  const [filterStatus, setFilterStatus] = useState<IssueStatus | ''>('');
  const [filterType, setFilterType] = useState<IssueType | ''>('');
  const [filterSeverity, setFilterSeverity] = useState<IssueSeverity | ''>('');
  const [filterSearch, setFilterSearch] = useState('');
  const hasActiveFilters = Boolean(filterStatus || filterType || filterSeverity || filterSearch);

  // ── Filter dropdown state ──────────────────────────
  type FilterDropdown = 'status' | 'type' | 'severity' | null;
  const [openFilterDropdown, setOpenFilterDropdown] = useState<FilterDropdown>(null);
  const statusBtnRef = useRef<HTMLButtonElement>(null);
  const typeBtnRef = useRef<HTMLButtonElement>(null);
  const severityBtnRef = useRef<HTMLButtonElement>(null);
  const [statusPos, setStatusPos] = useState({ top: 0, left: 0 });
  const [typePos, setTypePos] = useState({ top: 0, left: 0 });
  const [severityPos, setSeverityPos] = useState({ top: 0, left: 0 });

  const openFilterDd = useCallback((which: FilterDropdown) => {
    const refMap = { status: statusBtnRef, type: typeBtnRef, severity: severityBtnRef };
    const setMap = { status: setStatusPos, type: setTypePos, severity: setSeverityPos };
    if (!which) return;
    const ref = refMap[which];
    const setPos = setMap[which];
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left });
    }
    setOpenFilterDropdown(prev => prev === which ? null : which);
  }, []);

  useEffect(() => {
    if (!openFilterDropdown) return;
    const close = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-problems-btn]') || target.closest('[data-problems-dropdown]')) return;
      setOpenFilterDropdown(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openFilterDropdown]);

  // Auto-select from URL ?id= or global context
  useEffect(() => {
    const urlId = searchParams.get('id');
    const targetId = urlId ?? selectedProblemId;
    if (targetId && !selectedIssueId) {
      setSelectedIssueId(targetId);
      setShowDetailPanel(true);
      if (selectedProblemId) selectProblem(null); // consume
    }
  }, [searchParams, selectedProblemId, selectedIssueId, selectProblem]);

  const queryFilters = useMemo<IssueFilters>(() => ({
    project_id: selectedProject?.id,
    ...(urlPlanId && { plan_id: urlPlanId }),
    ...(filterStatus && { status: filterStatus as IssueStatus }),
    ...(filterType && { type: filterType as IssueType }),
    ...(filterSeverity && { severity: filterSeverity as IssueSeverity }),
  }), [selectedProject?.id, urlPlanId, filterStatus, filterType, filterSeverity]);

  const issuesQuery = useQuery({
    queryKey: ['issues', organizationId, selectedProject?.id, urlPlanId, queryFilters],
    enabled: Boolean(organizationId),
    queryFn: () => IssueService.list(organizationId, queryFilters),
    staleTime: 30_000,
  });

  const issuesRaw = issuesQuery.data ?? [];
  const issues = useMemo(() => {
    if (!filterSearch.trim()) return issuesRaw;
    const q = filterSearch.toLowerCase();
    return issuesRaw.filter(i =>
      i.title?.toLowerCase().includes(q) ||
      i.description?.toLowerCase().includes(q) ||
      i.code?.toLowerCase().includes(q)
    );
  }, [issuesRaw, filterSearch]);
  const selectedIssue = issues.find(i => i.id === selectedIssueId) ?? null;

  // ── Alerts query ───────────────────────────────────
  const alertsQuery = useQuery({
    queryKey: ['issue-alerts', organizationId],
    enabled: Boolean(organizationId),
    queryFn: () => IssueService.generateAlerts(organizationId),
    staleTime: 60_000,
  });

  const [alerts, setAlerts] = useState<ProblemAlert[]>([]);
  const [recurrences, setRecurrences] = useState<RecurrenceMatch[]>([]);

  // Sync alerts from query
  React.useEffect(() => {
    if (alertsQuery.data) setAlerts(alertsQuery.data);
  }, [alertsQuery.data]);

  const handleDismissAlert = useCallback((alertId: string) => {
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, dismissed: true } : a));
  }, []);

  const handleNavigateToIssue = useCallback((issueId: string) => {
    setSelectedIssueId(issueId);
    setShowDetailPanel(true);
  }, []);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['issues'] });
    queryClient.invalidateQueries({ queryKey: ['issue-alerts'] });
    queryClient.invalidateQueries({ queryKey: ['ops-problems'] });
    queryClient.invalidateQueries({ queryKey: ['ops-metrics'] });
  }, [queryClient]);

  // ── Command: issue.create (modal) ─────────────────
  const handleOpenCreateModal = useCallback(() => {
    setNewTitle('');
    setNewType('incident');
    setNewSeverity('medium');
    setNewDescription('');
    setNewMode('quick_correction');
    setNewDepartment('');
    setRecurrences([]);
    setShowCreateModal(true);
  }, []);

  // ── Recurrence check before create ────────────────
  const handleCheckRecurrence = useCallback(async () => {
    if (!newType) return;
    try {
      const matches = await IssueService.detectRecurrence(organizationId, {
        type: newType,
        root_cause: newDescription.trim() || null,
      });
      setRecurrences(matches);
    } catch {
      // Non-blocking — silent fail
    }
  }, [organizationId, newType, newDescription]);

  const handleSubmitCreate = useCallback(async () => {
    if (!newTitle.trim()) {
      showToast('warning', 'Informe o título do problema');
      return;
    }
    setCreateLoading(true);
    try {
      // Check recurrence before creating
      const matches = await IssueService.detectRecurrence(organizationId, {
        type: newType,
        root_cause: newDescription.trim() || null,
      });
      if (matches.length > 0) {
        setRecurrences(matches);
      }

      await IssueService.create(organizationId, {
        title: newTitle.trim(),
        type: newType,
        severity: newSeverity,
        description: newDescription.trim() || null,
        status: 'open',
        mode: newMode,
        department: newDepartment.trim() || null,
      });
      showToast('success', 'Problema criado com sucesso');
      setShowCreateModal(false);
      handleRefresh();
    } catch (err) {
      console.error('[Issues] create failed:', err);
      showToast('error', 'Erro ao criar problema');
    } finally {
      setCreateLoading(false);
    }
  }, [organizationId, newTitle, newType, newSeverity, newDescription, newMode, newDepartment, handleRefresh, showToast]);

  // ── Command: issue.close ───────────────────────────
  const handleCloseIssue = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssueId, 'closed');
      showToast('success', 'Problema encerrado');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] close failed:', err);
      showToast('error', 'Erro ao encerrar problema');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);



  // ── Command: issue.validate ────────────────────────
  const handleValidate = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssueId, 'validation');
      showToast('info', 'Validação iniciada');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] validate failed:', err);
      showToast('error', 'Erro ao iniciar validação');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);

  // ── Command: issue.monitor ─────────────────────────
  const handleMonitor = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssueId, 'monitoring');
      showToast('info', 'Monitoramento iniciado');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] monitor failed:', err);
      showToast('error', 'Erro ao iniciar monitoramento');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);

  // ── Command: issue.cancel ──────────────────────────
  const handleCancel = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      await IssueService.update(organizationId, selectedIssueId, { status: 'open' });
      showToast('warning', 'Problema reaberto');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] cancel failed:', err);
      showToast('error', 'Erro ao reabrir problema');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);

  // ── Command: issue.assign (assume responsibility) ──
  const handleAssign = useCallback(async () => {
    if (!selectedIssueId) return;
    try {
      // TODO: Replace with actual user name from auth context
      await IssueService.update(organizationId, selectedIssueId, { assigned_to: 'Eu' });
      showToast('success', 'Problema assumido');
      handleRefresh();
    } catch (err) {
      console.error('[Issues] assign failed:', err);
      showToast('error', 'Erro ao assumir problema');
    }
  }, [organizationId, selectedIssueId, handleRefresh, showToast]);

  // ── Command: issue.advanceStage (contextual MASP forward) ──
  const handleAdvanceStage = useCallback(async () => {
    if (!selectedIssue) return;
    const stageOrder = getStageOrder(selectedIssue.mode);
    const allowed = getStatusTransitions(selectedIssue.mode)[selectedIssue.status] ?? [];
    const currentIdx = stageOrder.indexOf(selectedIssue.status);
    const nextStage = allowed.find(s => stageOrder.indexOf(s) > currentIdx);
    if (!nextStage) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssue.id, nextStage);
      showToast('success', `Avançado para ${ISSUE_STATUS_LABELS[nextStage]}`);
      handleRefresh();
    } catch (err) {
      console.error('[Issues] advance failed:', err);
      showToast('error', 'Erro ao avançar etapa');
    }
  }, [organizationId, selectedIssue, handleRefresh, showToast]);

  // ── Command: issue.retreatStage (MASP backward) ────
  const handleRetreatStage = useCallback(async () => {
    if (!selectedIssue) return;
    const stageOrder = getStageOrder(selectedIssue.mode);
    const allowed = getStatusTransitions(selectedIssue.mode)[selectedIssue.status] ?? [];
    const currentIdx = stageOrder.indexOf(selectedIssue.status);
    const prevStage = [...allowed].reverse().find(s => stageOrder.indexOf(s) < currentIdx);
    if (!prevStage) return;
    try {
      await IssueService.transitionStatus(organizationId, selectedIssue.id, prevStage);
      showToast('info', `Retrocedido para ${ISSUE_STATUS_LABELS[prevStage]}`);
      handleRefresh();
    } catch (err) {
      console.error('[Issues] retreat failed:', err);
      showToast('error', 'Erro ao retroceder etapa');
    }
  }, [organizationId, selectedIssue, handleRefresh, showToast]);

  // ── Command: issue.createLinkedAction ───────────────
  const handleCreateLinkedAction = useCallback(() => {
    if (!selectedIssue) return;
    navigate('/acoes/nova', {
      state: {
        fromIssue: {
          id: selectedIssue.id,
          title: selectedIssue.title,
          severity: selectedIssue.severity,
          description: selectedIssue.description,
        },
      },
    });
  }, [selectedIssue, navigate]);

  // ── Command: export.excel ──────────────────────────
  const handleExportExcel = useCallback(() => {
    const exportData = issues.map(i => ({
      title: i.title,
      type: ISSUE_TYPE_LABELS[i.type],
      severity: ISSUE_SEVERITY_LABELS[i.severity],
      status: ISSUE_STATUS_LABELS[i.status],
      assigned_to: i.assigned_to ?? '',
      created_at: new Date(i.created_at).toLocaleDateString('pt-BR'),
    }));
    exportToCSV(exportData, EXPORT_COLUMNS, 'problemas');
    showToast('success', 'Exportação Excel gerada');
  }, [issues, showToast]);

  // ── Command: export.pdf ────────────────────────────
  const handleExportPDF = useCallback(() => {
    const exportData = issues.map(i => ({
      title: i.title,
      type: ISSUE_TYPE_LABELS[i.type],
      severity: ISSUE_SEVERITY_LABELS[i.severity],
      status: ISSUE_STATUS_LABELS[i.status],
      assigned_to: i.assigned_to ?? '',
      created_at: new Date(i.created_at).toLocaleDateString('pt-BR'),
    }));
    exportToPrintPDF(exportData, EXPORT_COLUMNS, 'Gestão de Problemas');
    showToast('info', 'PDF gerado para impressão');
  }, [issues, showToast]);

  // ── Command: filter toggle ─────────────────────────
  const handleClearFilters = useCallback(() => {
    setFilterStatus('');
    setFilterType('');
    setFilterSeverity('');
    setFilterSearch('');
  }, []);

  const handleClearProjectFilter = useCallback(() => {
    setSelectedProject(null);
    showToast('info', 'Filtro de projeto removido. Exibindo todos os problemas.');
  }, [setSelectedProject, showToast]);

  // ── Command: dashboard toggle ──────────────────────
  const handleToggleDashboard = useCallback(() => {
    setShowDashboard(prev => !prev);
  }, []);

  const problemasFiltersWidget = useMemo(() => (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-1.5">
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value as IssueStatus | '')}
          title="Filtrar por status"
          className={desktopRibbonSelectClass}
        >
          <option value="">Todos</option>
          {KANBAN_STATUSES.map(s => <option key={s} value={s}>{ISSUE_STATUS_LABELS[s]}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <select
          value={filterType}
          onChange={e => setFilterType(e.target.value as IssueType | '')}
          title="Filtrar por tipo"
          className={desktopRibbonSelectClass}
        >
          <option value="">Todos</option>
          {(Object.keys(ISSUE_TYPE_LABELS) as IssueType[]).map(t => <option key={t} value={t}>{ISSUE_TYPE_LABELS[t]}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="h-2 w-2 rounded-full bg-red-500/80" />
        <select
          value={filterSeverity}
          onChange={e => setFilterSeverity(e.target.value as IssueSeverity | '')}
          title="Filtrar por severidade"
          className={desktopRibbonSelectClass}
        >
          <option value="">Todas</option>
          {(Object.keys(ISSUE_SEVERITY_LABELS) as IssueSeverity[]).map(s => <option key={s} value={s}>{ISSUE_SEVERITY_LABELS[s]}</option>)}
        </select>
      </div>
      {hasActiveFilters && (
        <button onClick={handleClearFilters} className="text-[10px] text-blue-400 hover:text-blue-300">× limpar</button>
      )}
    </div>
  ), [filterStatus, filterType, filterSeverity, hasActiveFilters, handleClearFilters]);

  // ── Ribbon (hook-based MasterRibbon) ────────────
  const ribbonConfig = useProblemasRibbon({
    selectedIssue,
    viewMode,
    showDashboard,
    hasActiveFilters,
    onNewIssue: handleOpenCreateModal,
    onAssign: handleAssign,
    onAdvanceStage: handleAdvanceStage,
    onRetreatStage: handleRetreatStage,
    onCreateLinkedAction: handleCreateLinkedAction,
    onValidate: handleValidate,
    onMonitor: handleMonitor,
    onCloseIssue: handleCloseIssue,
    onCancelIssue: handleCancel,
    onViewModeChange: setViewMode,
    onToggleDashboard: handleToggleDashboard,
    onRefresh: handleRefresh,
    onExportPDF: handleExportPDF,
    onExportExcel: handleExportExcel,
    afterGroups: problemasFiltersWidget,
  });

  const filterBtnClass = 'flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2 text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';
  const dropdownPanelClass = 'ribbon-dropdown-panel absolute z-[9999] min-w-[160px] overflow-hidden rounded-[5px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1 shadow-2xl';
  const dropdownItemClass = 'flex w-full items-center gap-2 px-3 py-[6px] text-left text-[12px] text-[var(--text-primary)] transition-colors hover:bg-[var(--button-hover)]';

  const activeStatusLabel = filterStatus ? ISSUE_STATUS_LABELS[filterStatus] : 'Todos';
  const activeTypeLabel = filterType ? ISSUE_TYPE_LABELS[filterType] : 'Todos';
  const activeSeverityLabel = filterSeverity ? ISSUE_SEVERITY_LABELS[filterSeverity] : 'Todas';

  const filtersWidget = useMemo(() => (
    <div className="flex items-center gap-1.5">
      {selectedProject && (
        <button
          type="button"
          onClick={handleClearProjectFilter}
          className="flex h-[30px] shrink-0 items-center gap-1.5 rounded-[3px] border border-blue-300/50 bg-blue-500/10 px-2 text-[11px] text-blue-700 transition-colors hover:bg-blue-500/15 dark:border-blue-500/30 dark:text-blue-300"
          title={`Projeto ativo: ${selectedProject.name}. Clique para ver todos.`}
        >
          <Building2 size={12} />
          <span className="max-w-[140px] truncate">{selectedProject.name}</span>
          <span className="opacity-80">• Ver todos</span>
        </button>
      )}

      {/* Status */}
      <button ref={statusBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('status')}>
        <Filter size={12} className="text-[var(--text-secondary)]" />
        <span>{activeStatusLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'status' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: statusPos.top, left: statusPos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { setFilterStatus(''); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={!filterStatus ? 'opacity-100' : 'opacity-0'} />
          </button>
          {Object.entries(ISSUE_STATUS_LABELS).map(([k, v]) => (
            <button key={k} type="button" className={dropdownItemClass} onClick={() => { setFilterStatus(k as IssueStatus); setOpenFilterDropdown(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: k === 'open' ? '#ef4444' : k === 'closed' ? '#22c55e' : '#3b82f6' }} />
              <span className="flex-1">{v}</span>
              <Check size={11} className={filterStatus === k ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Type */}
      <button ref={typeBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('type')}>
        <Crosshair size={12} className="text-[var(--text-secondary)]" />
        <span>{activeTypeLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'type' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: typePos.top, left: typePos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { setFilterType(''); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todos</span>
            <Check size={11} className={!filterType ? 'opacity-100' : 'opacity-0'} />
          </button>
          {Object.entries(ISSUE_TYPE_LABELS).map(([k, v]) => (
            <button key={k} type="button" className={dropdownItemClass} onClick={() => { setFilterType(k as IssueType); setOpenFilterDropdown(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0" />
              <span className="flex-1">{v}</span>
              <Check size={11} className={filterType === k ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Severity */}
      <button ref={severityBtnRef} data-problems-btn="" type="button" className={filterBtnClass} onClick={() => openFilterDd('severity')}>
        <AlertCircle size={12} className="text-[var(--text-secondary)]" />
        <span>{activeSeverityLabel}</span>
        <ChevronDown size={10} className="text-[var(--text-secondary)]" />
      </button>
      {openFilterDropdown === 'severity' && createPortal(
        <div data-problems-dropdown="" className={dropdownPanelClass} style={{ position: 'fixed', top: severityPos.top, left: severityPos.left }}>
          <button type="button" className={dropdownItemClass} onClick={() => { setFilterSeverity(''); setOpenFilterDropdown(null); }}>
            <span className="inline-block h-[7px] w-[7px] shrink-0" />
            <span className="flex-1">Todas</span>
            <Check size={11} className={!filterSeverity ? 'opacity-100' : 'opacity-0'} />
          </button>
          {Object.entries(ISSUE_SEVERITY_LABELS).map(([k, v]) => (
            <button key={k} type="button" className={dropdownItemClass} onClick={() => { setFilterSeverity(k as IssueSeverity); setOpenFilterDropdown(null); }}>
              <span className="inline-block h-[7px] w-[7px] shrink-0 rounded-full" style={{ background: k === 'critical' ? '#ef4444' : k === 'high' ? '#f97316' : k === 'medium' ? '#3b82f6' : '#94a3b8' }} />
              <span className="flex-1">{v}</span>
              <Check size={11} className={filterSeverity === k ? 'opacity-100' : 'opacity-0'} />
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Search */}
      <div className="flex w-[220px] shrink-0 items-center">
        <div className="flex w-full items-center gap-2 rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] px-2">
          <Search size={14} className="shrink-0 text-[var(--text-secondary)]" />
          <input
            type="text"
            placeholder="Buscar problema..."
            value={filterSearch}
            onChange={e => setFilterSearch(e.target.value)}
            className={`w-full ${desktopRibbonInputClass} border-0 bg-transparent px-0`}
          />
          {filterSearch && (
            <button onClick={() => setFilterSearch('')} className="shrink-0 rounded p-0.5 hover:bg-[var(--button-hover)]">
              <X size={10} className="text-[var(--text-secondary)]" />
            </button>
          )}
        </div>
      </div>
    </div>
  ), [filterStatus, filterType, filterSeverity, filterSearch, openFilterDropdown, statusPos, typePos, severityPos, activeStatusLabel, activeTypeLabel, activeSeverityLabel, openFilterDd, handleClearFilters, selectedProject, handleClearProjectFilter]);

  const ribbonWithFilters = useMemo(() => ({
    ...ribbonConfig,
    slots: {
      ...ribbonConfig.slots,
      afterGroups: filtersWidget,
    },
  }), [ribbonConfig, filtersWidget]);

  // ── Dashboard stats ────────────────────────────────
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of KANBAN_STATUSES) counts[s] = 0;
    for (const i of issues) counts[i.status] = (counts[i.status] || 0) + 1;
    return counts;
  }, [issues]);

  const severityCounts = useMemo(() => {
    const counts: Record<string, number> = { critical: 0, high: 0, medium: 0, low: 0 };
    for (const i of issues) counts[i.severity] = (counts[i.severity] || 0) + 1;
    return counts;
  }, [issues]);

  const avgResolutionDays = useMemo(() => {
    const closed = issues.filter(i => i.status === 'closed' && i.resolved_at);
    if (closed.length === 0) return null;
    const total = closed.reduce((acc, i) => {
      const created = new Date(i.created_at).getTime();
      const resolved = new Date(i.resolved_at!).getTime();
      return acc + (resolved - created) / (1000 * 60 * 60 * 24);
    }, 0);
    return Math.round((total / closed.length) * 10) / 10;
  }, [issues]);

  const avgStageTimeDays = useMemo(() => {
    const resolved = issues.filter(i => i.status === 'closed' && i.resolved_at);
    if (resolved.length === 0) return null;
    const stageCount = 7; // open → investigation → root_cause → action_plan → execution → validation → monitoring → closed
    const totalDays = resolved.reduce((acc, i) => {
      const created = new Date(i.created_at).getTime();
      const res = new Date(i.resolved_at!).getTime();
      return acc + (res - created) / (1000 * 60 * 60 * 24);
    }, 0);
    return Math.round((totalDays / resolved.length / stageCount) * 10) / 10;
  }, [issues]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const t of Object.keys(ISSUE_TYPE_LABELS)) counts[t] = 0;
    for (const i of issues) counts[i.type] = (counts[i.type] || 0) + 1;
    return counts;
  }, [issues]);

  const departmentCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const i of issues) {
      const dept = (i as any).department || 'Não definido';
      counts[dept] = (counts[dept] || 0) + 1;
    }
    return counts;
  }, [issues]);

  const modeCounts = useMemo(() => {
    const counts = { quick_correction: 0, structured_problem: 0 };
    for (const i of issues) {
      const mode = (i as any).mode || 'quick_correction';
      if (mode in counts) counts[mode as keyof typeof counts]++;
    }
    return counts;
  }, [issues]);

  // ── Status capsules for PageHeader ─────────────────
  const CAPSULE_COLOR_MAP: Record<string, string> = {
    open: 'red', investigation: 'amber', root_cause: 'amber', action_plan: 'blue',
    execution: 'purple', validation: 'sky', monitoring: 'sky', closed: 'emerald',
  };
  const handleCapsuleClick = useCallback((status: IssueStatus) => {
    setFilterStatus(prev => prev === status ? '' : status);
  }, []);

  const statusItems = useMemo<StatusCapsuleItem[]>(() =>
    (['open', 'investigation', 'execution', 'monitoring', 'closed'] as IssueStatus[]).map(s => ({
      label: ISSUE_STATUS_LABELS[s],
      value: statusCounts[s] ?? 0,
      color: CAPSULE_COLOR_MAP[s] ?? 'slate',
      onClick: () => handleCapsuleClick(s),
      active: filterStatus === s,
    })),
  [statusCounts, filterStatus, handleCapsuleClick]);

  // ── Kanban status change handler ────────────────────
  const handleKanbanStatusChange = useCallback(async (itemId: string, newStatus: string) => {
    try {
      await IssueService.transitionStatus(organizationId, itemId, newStatus as IssueStatus);
      handleRefresh();
    } catch (err) {
      console.error('[Issues] kanban status change failed:', err);
      showToast('error', `Transição inválida para ${ISSUE_STATUS_LABELS[newStatus as IssueStatus] ?? newStatus}`);
    }
  }, [organizationId, handleRefresh, showToast]);

  // ── Kanban card renderer ───────────────────────────
  const renderIssueCard = useCallback((issue: Issue & KanbanCardData) => {
    const isSelected = selectedIssueId === issue.id;
    const daysSinceCreation = Math.floor((Date.now() - new Date(issue.created_at).getTime()) / (1000 * 60 * 60 * 24));
    const issueMode = (issue as any).mode as IssueMode | undefined;
    const linkedCount = (issue as any).linked_actions_count as number | undefined;

    return (
      <button
        type="button"
        onClick={() => { setSelectedIssueId(issue.id); setShowDetailPanel(true); }}
        className={`w-full cursor-grab rounded border bg-white p-3 text-left transition active:cursor-grabbing dark:bg-slate-800/80 ${
          isSelected
            ? 'border-blue-400 ring-1 ring-blue-400 dark:border-blue-500'
            : 'border-slate-200 hover:border-slate-300 hover:shadow-sm dark:border-slate-700 dark:hover:border-slate-600'
        }`}
      >
        {/* Mode indicator */}
        {issueMode === 'structured_problem' && (
          <div className="mb-1.5 flex items-center gap-1">
            <Crosshair size={9} className="text-orange-500" />
            <span className="text-[8px] font-semibold uppercase tracking-wider text-orange-600 dark:text-orange-400">MASP</span>
          </div>
        )}

        {/* Title */}
        <p className="line-clamp-2 text-[12px] font-medium text-slate-800 dark:text-slate-200">{issue.title}</p>

        {/* Badges row: severity + type */}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <EnterpriseBadge label={ISSUE_SEVERITY_LABELS[issue.severity]} color={SEVERITY_COLOR[issue.severity]} />
          <span className="text-[9px] text-slate-400 dark:text-slate-500">{ISSUE_TYPE_LABELS[issue.type]}</span>
        </div>

        {/* Footer: responsible + time + actions */}
        <div className="mt-2.5 flex items-center gap-2 border-t border-slate-100 pt-2 dark:border-slate-700/60">
          {issue.assigned_to ? (
            <div className="flex items-center gap-1 text-[10px] text-slate-600 dark:text-slate-400">
              <User size={10} />
              <span className="max-w-[80px] truncate">{issue.assigned_to}</span>
            </div>
          ) : (
            <span className="text-[10px] text-slate-400 dark:text-slate-500">Sem resp.</span>
          )}
          <div className="ml-auto flex items-center gap-2">
            {linkedCount != null && linkedCount > 0 && (
              <div className="flex items-center gap-0.5 text-[10px] text-blue-500 dark:text-blue-400" title={`${linkedCount} ação(ões) vinculada(s)`}>
                <LinkIcon size={9} />
                <span>{linkedCount}</span>
              </div>
            )}
            <div className="flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500" title={`Aberto há ${daysSinceCreation} dia(s)`}>
              <Clock size={10} />
              <span>{daysSinceCreation}d</span>
            </div>
          </div>
        </div>
      </button>
    );
  }, [selectedIssueId]);

  // ── Kanban items (cast to KanbanCardData) ──────────
  const kanbanItems = useMemo(() =>
    issues.map(i => ({ ...i, status: i.status } as Issue & KanbanCardData)),
    [issues],
  );

  // ── Kanban View ────────────────────────────────────
  const kanbanView = (
    <KanbanBoard<Issue & KanbanCardData>
      columns={KANBAN_COLUMNS}
      items={kanbanItems}
      onStatusChange={handleKanbanStatusChange}
      renderCard={renderIssueCard}
      className="min-h-[400px]"
    />
  );

  // ── Dashboard View (collapsible) ────────────────────
  const dashboardView = (
    <div className="mb-4 rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800/60">
      {/* Dashboard header with collapse toggle */}
      <button
        type="button"
        onClick={handleToggleDashboard}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left hover:bg-slate-50/50 dark:hover:bg-slate-700/20"
      >
        <BarChart3 size={14} className="text-slate-500 dark:text-slate-400" />
        <h3 className="text-[12px] font-bold text-slate-800 dark:text-slate-200">Dashboard de Problemas</h3>
        <ChevronUp size={14} className="ml-auto text-slate-400" />
      </button>

      <div className="border-t border-slate-100 px-4 pb-4 pt-3 dark:border-slate-700/60">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Abertos</p>
            <p className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">{statusCounts.open ?? 0}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Críticos</p>
            <p className="mt-1 text-xl font-bold text-red-600 dark:text-red-400">{severityCounts.critical ?? 0}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Em Andamento</p>
            <p className="mt-1 text-xl font-bold text-amber-600 dark:text-amber-400">
              {(statusCounts.investigation ?? 0) + (statusCounts.root_cause ?? 0) + (statusCounts.action_plan ?? 0) + (statusCounts.execution ?? 0) + (statusCounts.validation ?? 0) + (statusCounts.monitoring ?? 0)}
            </p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Encerrados</p>
            <p className="mt-1 text-xl font-bold text-emerald-600 dark:text-emerald-400">{statusCounts.closed ?? 0}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">MTTR (dias)</p>
            <p className="mt-1 text-xl font-bold text-blue-600 dark:text-blue-400">{avgResolutionDays ?? '—'}</p>
          </div>
          <div className="rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Tempo Médio/Etapa</p>
            <p className="mt-1 text-xl font-bold text-violet-600 dark:text-violet-400">{avgStageTimeDays ?? '—'}<span className="ml-0.5 text-[10px] font-normal text-slate-400">d</span></p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-4">
          {/* Severity breakdown */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Severidade</p>
            <div className="space-y-1.5">
              {(['critical', 'high', 'medium', 'low'] as const).map(sev => {
                const count = severityCounts[sev];
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={sev} className="flex items-center gap-2">
                    <span className="w-16 text-[10px] text-slate-600 dark:text-slate-400">{ISSUE_SEVERITY_LABELS[sev]}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div
                        className={`h-full rounded-full transition-all ${
                          sev === 'critical' ? 'bg-red-500' : sev === 'high' ? 'bg-orange-500' : sev === 'medium' ? 'bg-yellow-500' : 'bg-slate-400'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Status distribution */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Status</p>
            <div className="space-y-1.5">
              {KANBAN_STATUSES.map(s => {
                const count = statusCounts[s] ?? 0;
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <span className="w-20 text-[10px] text-slate-600 dark:text-slate-400">{ISSUE_STATUS_LABELS[s]}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Type */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Tipo</p>
            <div className="space-y-1.5">
              {(Object.keys(ISSUE_TYPE_LABELS) as IssueType[]).map(t => {
                const count = typeCounts[t] ?? 0;
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={t} className="flex items-center gap-2">
                    <span className="w-24 truncate text-[10px] text-slate-600 dark:text-slate-400">{ISSUE_TYPE_LABELS[t]}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div className="h-full rounded-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* By Department */}
          <div>
            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">Por Departamento</p>
            <div className="space-y-1.5">
              {Object.entries(departmentCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([dept, count]) => {
                const pct = issues.length > 0 ? (count / issues.length) * 100 : 0;
                return (
                  <div key={dept} className="flex items-center gap-2">
                    <span className="w-24 truncate text-[10px] text-slate-600 dark:text-slate-400">{dept}</span>
                    <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-700">
                      <div className="h-full rounded-full bg-teal-500 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="w-6 text-right text-[10px] font-semibold text-slate-600 dark:text-slate-400">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Filter Panel removed — filters are now inline in the Ribbon

  return (
    <ModuleLayout ribbonConfig={ribbonWithFilters}>
      <div className="flex h-full flex-col">
        {/* Main content area */}
        <div className="flex min-h-0 flex-1">
        <div className={`flex-1 overflow-auto transition-all ${showDetailPanel && selectedIssue ? 'mr-0' : ''}`}>
          <EnterprisePage
            title=""
            subtitle=""
          >
            {showDashboard && dashboardView}

            {/* Alerts bar */}
            <ProblemAlertsBar
              alerts={alerts}
              recurrences={[]}
              onDismissAlert={handleDismissAlert}
              onNavigateToIssue={handleNavigateToIssue}
            />

            {issuesQuery.isLoading ? (
              <div className="flex items-center justify-center py-20 text-sm text-slate-400">Carregando...</div>
            ) : viewMode === 'kanban' ? (
              kanbanView
            ) : (
              <DataGridEnterprise<Issue>
                columns={COLUMNS}
                data={issues}
                rowKey={(r) => r.id}
                selectedKey={selectedIssueId}
                onRowClick={(r) => { setSelectedIssueId(r.id); setShowDetailPanel(true); }}
                emptyTitle={selectedProject ? 'Nenhum problema para o projeto selecionado' : 'Nenhum problema registrado'}
                emptySubtitle={selectedProject ? 'Clique em "Ver todos" no chip do projeto no ribbon para remover o filtro.' : 'Use o botão "Novo Problema" no ribbon para criar'}
              />
            )}
          </EnterprisePage>
        </div>

        {/* Detail Panel */}
        {showDetailPanel && selectedIssue && (
          <ProblemDetailPanel
            issue={selectedIssue}
            organizationId={organizationId}
            onClose={() => { setShowDetailPanel(false); setSelectedIssueId(null); }}
            onRefresh={handleRefresh}
            onToast={showToast}
          />
        )}
        </div>
      </div>

      {/* ── Create Issue Modal ────────────────────── */}
      <EnterpriseModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Novo Problema"
        subtitle="Registrar incidente, não-conformidade ou desvio"
        loading={createLoading}
        footer={
          <>
            <ModalSecondaryButton onClick={() => setShowCreateModal(false)} disabled={createLoading}>Cancelar</ModalSecondaryButton>
            <ModalPrimaryButton onClick={handleSubmitCreate} loading={createLoading}>Criar Problema</ModalPrimaryButton>
          </>
        }
      >
        <ModalField label="Título *">
          <input
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            placeholder="Descreva o problema..."
            className={modalInputClass}
            autoFocus
          />
        </ModalField>
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Modo">
            <select value={newMode} onChange={e => setNewMode(e.target.value as IssueMode)} className={modalSelectClass}>
              {(Object.entries(ISSUE_MODE_LABELS) as [IssueMode, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </ModalField>
          <ModalField label="Departamento">
            <input
              value={newDepartment}
              onChange={e => setNewDepartment(e.target.value)}
              placeholder="Ex: Produção, Qualidade..."
              className={modalInputClass}
            />
          </ModalField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Tipo">
            <select value={newType} onChange={e => setNewType(e.target.value as IssueType)} className={modalSelectClass}>
              {(Object.entries(ISSUE_TYPE_LABELS) as [IssueType, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </ModalField>
          <ModalField label="Severidade">
            <select value={newSeverity} onChange={e => setNewSeverity(e.target.value as IssueSeverity)} className={modalSelectClass}>
              {(Object.entries(ISSUE_SEVERITY_LABELS) as [IssueSeverity, string][]).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </ModalField>
        </div>
        {newMode === 'structured_problem' && (
          <div className="rounded border border-orange-200 bg-orange-50 px-3 py-2 dark:border-orange-800/40 dark:bg-orange-900/20">
            <p className="text-[10px] font-medium text-orange-700 dark:text-orange-400">
              <Crosshair size={10} className="mr-1 inline" />
              Problema Estruturado (MASP): Passará pelas 8 etapas — Abertura → Investigação → Causa Raiz → Plano de Ação → Execução → Validação → Monitoramento → Encerramento
            </p>
          </div>
        )}
        <ModalField label="Descrição">
          <textarea
            value={newDescription}
            onChange={e => setNewDescription(e.target.value)}
            onBlur={handleCheckRecurrence}
            placeholder="Detalhes, contexto, evidências..."
            className={`${modalInputClass} min-h-[80px] resize-y`}
            rows={3}
          />
        </ModalField>

        {/* Recurrence warnings */}
        {recurrences.length > 0 && (
          <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/20">
            <p className="mb-1.5 text-[10px] font-bold text-amber-700 dark:text-amber-400">
              ⚠ Possível recorrência detectada
            </p>
            <div className="space-y-1">
              {recurrences.map(match => (
                <div key={match.issue_id} className="flex items-center gap-2 text-[10px] text-amber-600 dark:text-amber-400/80">
                  <span className="font-medium">{Math.round(match.similarity_score * 100)}%</span>
                  <span className="truncate">{match.issue_title}</span>
                  <span className="ml-auto shrink-0 rounded bg-amber-200 px-1 py-0.5 text-[8px] font-semibold dark:bg-amber-800/40">
                    {match.matching_fields.join(', ')}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-1 text-[9px] text-amber-500 dark:text-amber-500">
              Verifique se este não é um problema recorrente antes de criar novo registro.
            </p>
          </div>
        )}
      </EnterpriseModal>

      <ToastContainer />
    </ModuleLayout>
  );
};
